import type { ColorOption, Product, ProductAttribute } from '@/models';
import { attributeSchema, colorOptionSchema } from '@/models';
import { getPublicSupabaseClient } from './supabase-client';

export interface ProductRepository {
  list(): Promise<Product[]>;
  findById(id: string): Promise<Product | null>;
  findBySlug(slug: string): Promise<Product | null>;
}

interface ImageRow {
  url: string;
  is_primary: boolean | null;
  display_order: number | null;
}

interface ProductRow {
  id: string;
  slug: string;
  name_en: string | null;
  name_ar: string;
  description_ar: string | null;
  description_en: string | null;
  price: number | string;
  show_price: boolean | null;
  is_featured: boolean | null;
  is_bestseller: boolean | null;
  image_url: string | null;
  attributes: unknown;
  colors: unknown;
  brands: { slug: string; name_en: string } | null;
  categories: { slug: string } | null;
  product_images: ImageRow[] | null;
}

const PRODUCT_COLUMNS = [
  'id',
  'slug',
  'name_en',
  'name_ar',
  'description_ar',
  'description_en',
  'price',
  'show_price',
  'is_featured',
  'is_bestseller',
  'image_url',
  'attributes',
  'colors',
  'brands(slug, name_en)',
  'categories(slug)',
  'product_images(url, is_primary, display_order)',
].join(', ');

const BADGE_TYPES = new Set(['samsung', 'apple', 'xiaomi', 'huawei']);

function parseAttributes(value: unknown): ProductAttribute[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((entry) => {
    const parsed = attributeSchema.safeParse(entry);
    return parsed.success ? [parsed.data] : [];
  });
}

function parseColors(value: unknown): ColorOption[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((entry) => {
    const parsed = colorOptionSchema.safeParse(entry);
    return parsed.success ? [parsed.data] : [];
  });
}

function toProduct(row: ProductRow): Product {
  const images = [...(row.product_images ?? [])].sort(
    (a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0) || (a.display_order ?? 0) - (b.display_order ?? 0)
  );
  const brandSlug = row.brands?.slug ?? '';

  return {
    id: row.id,
    slug: row.slug,
    name: row.name_en ?? row.name_ar,
    nameAr: row.name_ar,
    brandName: row.brands?.name_en ?? brandSlug,
    brandSlug,
    categorySlug: row.categories?.slug ?? '',
    price: Number(row.price),
    showPrice: row.show_price ?? true,
    descriptionAr: row.description_ar ?? '',
    descriptionEn: row.description_en ?? undefined,
    imageUrl: row.image_url || images[0]?.url || '/products/galaxy-s24-ultra.svg',
    badgeType: BADGE_TYPES.has(brandSlug) ? (brandSlug as Product['badgeType']) : undefined,
    specs: [],
    features: [],
    variants: [],
    isFeatured: row.is_featured ?? false,
    isBestseller: row.is_bestseller ?? false,
    attributes: parseAttributes(row.attributes),
    colors: parseColors(row.colors),
  };
}

export const supabaseProductRepository: ProductRepository = {
  async list() {
    const supabase = await getPublicSupabaseClient();
    if (!supabase) {
      return [];
    }

    const { data, error } = await supabase
      .from('products')
      .select(PRODUCT_COLUMNS)
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (error || !data) {
      console.error('Failed to load products:', error?.message);
      return [];
    }
    return (data as unknown as ProductRow[]).map(toProduct);
  },

  async findById(id) {
    const supabase = await getPublicSupabaseClient();
    if (!supabase) {
      return null;
    }

    const { data, error } = await supabase
      .from('products')
      .select(PRODUCT_COLUMNS)
      .eq('id', id)
      .maybeSingle();

    if (error || !data) {
      return null;
    }
    return toProduct(data as unknown as ProductRow);
  },

  async findBySlug(slug) {
    const supabase = await getPublicSupabaseClient();
    if (!supabase) {
      return null;
    }

    const { data, error } = await supabase
      .from('products')
      .select(PRODUCT_COLUMNS)
      .eq('slug', slug)
      .maybeSingle();

    if (error || !data) {
      return null;
    }
    return toProduct(data as unknown as ProductRow);
  },
};
